import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

/**
 * FallingHeartsCanvas — a full-viewport layer of gently falling rose + lavender
 * hearts with two delight moments:
 *   • Cursor-repel + bloom (hearts within 150px of the cursor scale up and drift away)
 *   • Click bursts (a mini radial explosion of 10 particles wherever the user clicks)
 *
 * Fixed to the viewport (`fixed inset-0 z-[40] pointer-events-none`) so it lives
 * on top of every page's content — the site can look through them like a snow
 * globe. Motion is disabled entirely for `prefers-reduced-motion: reduce`.
 */

// Glossy heart — SVG with linear gradient fill and a highlight ellipse for shine.
function GlossyHeart({ size = 24, color = "rose", strokeWidth = 1 }) {
    // Unique gradient IDs per rendered heart so multiple hearts co-exist
    // (React does this via key, but SVG gradient IDs need to be unique per node
    // — we use React.useId to avoid collisions).
    const uid = React.useId().replace(/:/g, "");
    const grad = `heartGrad-${uid}`;
    const shine = `heartShine-${uid}`;
    const stops = color === "rose"
        ? { top: "#FFE4EE", mid: "#FAB4CE", bot: "#E68AAF" }
        : { top: "#EDE5F8", mid: "#CFBFEE", bot: "#A594DE" };
    const stroke = color === "rose" ? "#CB7A9A" : "#9986CE";
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: "block", filter: `drop-shadow(0 4px 6px ${stroke}33)` }}>
            <defs>
                <linearGradient id={grad} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%"   stopColor={stops.top} />
                    <stop offset="55%"  stopColor={stops.mid} />
                    <stop offset="100%" stopColor={stops.bot} />
                </linearGradient>
                <radialGradient id={shine} cx="35%" cy="30%" r="30%">
                    <stop offset="0%"   stopColor="#FFFFFF" stopOpacity="0.9" />
                    <stop offset="60%"  stopColor="#FFFFFF" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
                </radialGradient>
            </defs>
            <path
                d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                fill={`url(#${grad})`}
                stroke={stroke}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            {/* Glossy shine highlight */}
            <ellipse cx="8.2" cy="7.5" rx="3.1" ry="2.2" fill={`url(#${shine})`} />
        </svg>
    );
}

// Deterministic seeded positions so hydration stays stable.
const HEARTS = Array.from({ length: 28 }, (_, i) => {
    const seed = (i * 7.7) % 100;
    const hueSeed = (i * 3) % 5;
    return {
        left: `${(seed * 1.03) % 100}%`,
        size: 16 + ((i * 5) % 24),          // 16–39px (a touch bigger for gloss to read)
        duration: 11 + ((i * 1.3) % 10),
        delay: (i * 0.7) % 14,
        drift: (i % 2 === 0 ? 1 : -1) * (10 + (i % 15)),
        color: hueSeed < 3 ? "rose" : "lavender",
        opacity: 0.55 + ((i % 5) * 0.06),   // 0.55–0.79 — lighter overall
    };
});

function ClickBurst({ x, y }) {
    const PARTICLES = 10;
    const items = Array.from({ length: PARTICLES }, (_, i) => {
        const angle = (i / PARTICLES) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
        const distance = 42 + Math.random() * 28;
        return {
            i, dx: Math.cos(angle) * distance, dy: Math.sin(angle) * distance,
            rose: i % 2 === 0, size: 10 + Math.random() * 8, rot: (Math.random() - 0.5) * 180,
        };
    });
    return (
        <div className="absolute" style={{ left: x, top: y, transform: "translate(-50%, -50%)" }} data-testid="hearts-burst">
            <motion.span aria-hidden className="absolute rounded-full"
                style={{
                    width: 20, height: 20, left: -10, top: -10,
                    background: "radial-gradient(circle, rgba(245,139,177,0.95) 0%, rgba(177,150,231,0.6) 55%, transparent 80%)",
                }}
                initial={{ scale: 0, opacity: 1 }}
                animate={{ scale: 3.2, opacity: 0 }}
                transition={{ duration: 0.55, ease: "easeOut" }} />
            {items.map(({ i, dx, dy, rose, size, rot }) => (
                <motion.span key={i} aria-hidden className="absolute block"
                    style={{ left: -size / 2, top: -size / 2 }}
                    initial={{ x: 0, y: 0, scale: 0.4, opacity: 1, rotate: 0 }}
                    animate={{ x: dx, y: dy, scale: 1, opacity: 0, rotate: rot }}
                    transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}>
                    <GlossyHeart size={size} color={rose ? "rose" : "lavender"} />
                </motion.span>
            ))}
        </div>
    );
}

function FallingHeart({ left, size, duration, delay, drift, color, opacity, reduced, registerRef, index }) {
    const outerRef = useRef(null);
    useEffect(() => {
        registerRef(index, outerRef.current);
        return () => registerRef(index, null);
    }, [registerRef, index]);
    if (reduced) return null;
    return (
        <motion.div ref={outerRef} aria-hidden
            className="absolute pointer-events-none will-change-transform"
            style={{ left, top: -40, opacity }}
            initial={{ y: -60, x: 0, rotate: -8 }}
            animate={{ y: "110vh", x: [0, drift, 0, -drift, 0], rotate: [-8, 8, -6, 8, -8] }}
            transition={{
                y: { duration, delay, repeat: Infinity, ease: "linear" },
                x: { duration: duration * 0.55, delay, repeat: Infinity, ease: "easeInOut" },
                rotate: { duration: duration * 0.4, delay, repeat: Infinity, ease: "easeInOut" },
            }}>
            <div style={{
                transform: "translate(var(--dx, 0px), var(--dy, 0px)) scale(var(--s, 1))",
                transition: "transform 0.55s cubic-bezier(0.34, 1.56, 0.64, 1)",
            }}>
                <GlossyHeart size={size} color={color} />
            </div>
        </motion.div>
    );
}

export default function FallingHeartsCanvas() {
    const reduced = useReducedMotion();
    const heartRefs = useRef([]);
    const registerHeartRef = useCallback((idx, el) => { heartRefs.current[idx] = el; }, []);
    const [bursts, setBursts] = useState([]);

    // Cursor-repel — direct DOM writes via CSS variables (zero re-renders).
    useEffect(() => {
        if (reduced) return;
        const RADIUS = 150, STRENGTH = 70;
        const onMove = (e) => {
            const mx = e.clientX, my = e.clientY;
            for (const el of heartRefs.current) {
                if (!el) continue;
                const inner = el.firstElementChild;
                if (!inner) continue;
                const r = el.getBoundingClientRect();
                const hx = r.left + r.width / 2, hy = r.top + r.height / 2;
                const dx = hx - mx, dy = hy - my;
                const d = Math.hypot(dx, dy);
                if (d < RADIUS && d > 1) {
                    const t = 1 - d / RADIUS;
                    inner.style.setProperty("--dx", `${(dx / d) * STRENGTH * t}px`);
                    inner.style.setProperty("--dy", `${(dy / d) * STRENGTH * t}px`);
                    inner.style.setProperty("--s", `${1 + t * 0.7}`);
                } else {
                    inner.style.setProperty("--dx", "0px");
                    inner.style.setProperty("--dy", "0px");
                    inner.style.setProperty("--s", "1");
                }
            }
        };
        const onLeave = () => {
            for (const el of heartRefs.current) {
                const inner = el?.firstElementChild;
                if (!inner) continue;
                inner.style.setProperty("--dx", "0px");
                inner.style.setProperty("--dy", "0px");
                inner.style.setProperty("--s", "1");
            }
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseleave", onLeave);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseleave", onLeave);
        };
    }, [reduced]);

    // Click bursts — global (window-level) so any scrollable page participates.
    // We temporarily enable pointer-events for the click, then read the event
    // and let it pass through. Simplest approach: use `pointerdown` on window,
    // spawn burst at fixed-viewport coords.
    useEffect(() => {
        if (reduced) return;
        const onDown = (e) => {
            // Ignore inputs / textareas / buttons within forms — the burst is a
            // background delight, not a form-interfering effect. We still fire
            // when the user clicks on plain content (headings, page background).
            const t = e.target;
            const tag = (t?.tagName || "").toLowerCase();
            if (["input", "textarea", "select"].includes(tag)) return;
            if (t?.closest?.("[data-hearts-off]")) return;
            const id = Date.now() + Math.random();
            setBursts((prev) => [...prev, { id, x: e.clientX, y: e.clientY }]);
            setTimeout(() => setBursts((prev) => prev.filter((b) => b.id !== id)), 900);
        };
        window.addEventListener("pointerdown", onDown);
        return () => window.removeEventListener("pointerdown", onDown);
    }, [reduced]);

    if (reduced) return null;

    return (
        <div aria-hidden className="fixed inset-0 z-[40] pointer-events-none overflow-hidden" data-testid="hearts-canvas">
            {HEARTS.map((h, i) => (
                <FallingHeart key={i} index={i} registerRef={registerHeartRef} {...h} reduced={reduced} />
            ))}
            <div className="absolute inset-0 pointer-events-none" data-testid="hearts-burst-layer">
                {bursts.map((b) => <ClickBurst key={b.id} x={b.x} y={b.y} />)}
            </div>
        </div>
    );
}
